import React, { createContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { navigate } from '@reach/router';
import {
  getI18nUrl,
  isCommsDetail,
  commsId,
  orgId as organizationId,
  getFromLocal,
  baseUrl,
  queryParam,
  cookies,
  redirectToLoginWithOrigin,
  memberApp,
  Ajax,
  SUPPORTED_LOCALES,
  qs,
  FE_VERSION,
  getCASToken
} from 'utils';
import { ConfigProvider } from '@eb/antd'
import enUS from '@eb/antd/es/locale/en_US'
import { getParam } from 'utils/ebUtils';
import { DEFAULTLANGUAGE, translationType } from 'constants/applicationConstant';
import ErrorPage from '../../containers/app/ErrorPage';
import CommsErrorPage from '../../containers/commsDetail/CommsErrorPage';
import { I18NProvider } from '../I18N';
import {
  useFetchBootData,
  useFetchAccountData,
  useChangeLang,
  useSplitsData,
  useFetchCommsData,
  useCSRFData
} from './api';

export const GlobalContext = createContext(null);

export const GlobalProvider = ({ children }) => {
  const [orgId, setOrgId] = useState(organizationId)
  const currentLanguage = useMemo(() => {
    const { lang: langFromUrl } = qs();
    const cookieLang = cookies.get('lang');
    if (langFromUrl) {
      const { lang: urlLang } = SUPPORTED_LOCALES.find(({lang: l}) =>
        l.toLowerCase() === langFromUrl.toLowerCase()) || {};
      if (urlLang) {
        cookies.set('lang', urlLang, { path: '/' });
        return urlLang;
      }
    }

    if (!cookieLang || cookieLang === 'undefined') {
      const browserLanguage = navigator.language || navigator.userLanguage;
      return SUPPORTED_LOCALES.find(({ browserLanguageCode }) =>
        browserLanguageCode === browserLanguage)?.lang || DEFAULTLANGUAGE;
    }

    return cookieLang;
  }, []);
  const [navConfig, setNavConfig] = useState();
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [lang, setLang] = useState(currentLanguage);
  const accessKey = qs()?.accessKey || '';
  const param = (accessKey === '') ? '' : `?accessKey=${accessKey}`;
  const [isInLoggedInEditProfileMode, setIsInLoggedInEditProfileMode] = useState(false);
  const loggedIn = navConfig ? navConfig.isLoggedIn : false;
  const [{
    data: bootData,
    loading: bootDataLoading,
    status: bootDataHttpStatus,
    error: bootDataError
  }, fetchBootData] = useFetchBootData({ orgId, accessKey, loggedIn });
  const [idToken, setIdToken] = useState(getFromLocal('idToken'));
  const [{ data: account, loading: accountDataLoading }, fetchAccountData]
        = useFetchAccountData({ orgId });
  const [{ data: splitData, loading: splitDataLoading }, fetchSplitData]
    = useSplitsData({ orgId });
      const [{ data: csrfResponse }, fetchCSRFData] = useCSRFData({ orgId });

  const [{ data: commsData }, fetchCommsData] = useFetchCommsData({ commsId })
  const [, putNewLangToServer] = useChangeLang();
  const [commsFetchStatus, setCommsFetchStatus] = useState(0);
  const fetchInitData = async () => {
    const hasAuth = getParam('Authorization') || getParam('accessKey');
    const promises = [fetchBootData(), fetchAccountData(), fetchSplitData(), fetchCSRFData()];
    if (hasAuth) {
      await Promise.all(promises);
    } else {
      promises.forEach(promise => promise);
    }
  };
  const [isPublicPortalEnabled, setIsPublicPortalEnabled] = useState(false);

  useEffect(() => {
    if (account?.publicProfileId) {
      setIsPublicPortalEnabled(true);
      sessionStorage.setItem('isPublicPortalEnabled', true);
    } else {
      setIsPublicPortalEnabled(false);
      sessionStorage.setItem('isPublicPortalEnabled', false);
    }
  }, [account?.publicProfileId]);
  useEffect(() => {
    // if commsDetail page, then fetch commsData
    // else init
    const init = async () => {
      if (!isCommsDetail) {
        await fetchInitData();
      } else {
        const response = await fetchCommsData();
        if (response.status !== 200) {
          setCommsFetchStatus(response.status);
        }
      }
    };
    init();
  }, []);

  useEffect(() => {
    // if commsDetail page, then set orgId from commsData
    const init = async () => {
      if (isCommsDetail && commsData && commsData.result) {
        setOrgId(commsData.result.organizationId);
        window.orgId = commsData.result.organizationId; // store orgId in window object
      }

      // if commsDetail page and commsData is fetched, then fetchInitData
      if (isCommsDetail
          && commsData
          && commsData.result
          && orgId
          && orgId === commsData.result.organizationId) {
        await fetchInitData();
      }
    };
    init();
  }, [commsData, orgId]);


  useEffect(() => {
    if (!bootData || accountDataLoading || bootDataLoading || splitDataLoading) {
      return;
    }
    const { pathname } = window.location;
    const {
      organization: {
        orgName,
        enabledSSO,
        showWeather,
        showIncidents,
        showNotificationsToMemberPortal,
        isPublic,
        showEvent: initShowEvent,
        showFAQs,
        showOverview,
        showCrisisTask,
        showCrisisSingleEventDashboard,
        showCrisisSurvey,
        translation,
        showPublicFilesLibrary,
        showTravel,
        isEB360MapEnabled = true
      },
    } = bootData;
    const {
      id: accountId,
      showOnCallSchedule = false,
      showDirectoryChat,
      showEvent: accountShowEvent,
      CSRF_TOKEN_FOR_SESSION = ''
    } = account || {};
    
    const { ebs_trm_enableSecuredTravelEducationPortal } = splitData || {};

    if (/^\/\d+\/(home|my-profile|my-location|my-subscription|my-additionalInformation|chat|incidents)$/.test(pathname) && !accountId) {
      window.location.href = `${baseUrl}/${orgId}/login${param}`;
    }

    if (/^\/\d+\/(portal-home)(\/.*)*$/.test(window.location.pathname)) {
      sessionStorage.setItem('entryFromPublicPortal', true);
    }
    if (/^\/\d+\/(home)(\/.*)*$/.test(window.location.pathname)) {
      sessionStorage.setItem('entryFromPublicPortal', false);
      if(getParam('Authorization')){
        window.location.href = `${baseUrl}/${orgId}/home${param}`;
      }
    }

    window.isPublic = isPublic
    const notifLoginRequired =
        showNotificationsToMemberPortal
        && !isPublic
        && !accountId;
    const taskLoginRequired =
        (showCrisisTask || showCrisisSingleEventDashboard)
        && !accountId;
    const incidentsLoginRequired = showIncidents && !accountId;

    const isLoginRequired = () => {
      if (/^\/\d+\/notif$/.test(pathname)) {
        return !showWeather && notifLoginRequired;
      }

      if (/^\/\d+\/notif\/.+$/.test(pathname)) {
        return notifLoginRequired;
      }

      if (/^\/\d+\/(task|cm)(\/.+)?$/.test(pathname)) {
        return taskLoginRequired;
      }

      if (/^\/\d+\/incidents(\/.+)?$/.test(pathname)) {
        return incidentsLoginRequired;
      }

      if (/^\/\d+\/travel$/.test(pathname)) {
        return !accountId;
      }

      return false;
    };

    // feature:['notifications to member portal'] belongs a private org
    //   so if acount-info has not be found from session, then redirect to /login
    if (isLoginRequired()) {
      const { origin, pathname: uri, hash, search } = window.location;
      // encode the search param for the original url
      const originUrl = (getParam('accessKey') === '') ? `${origin}${uri}${hash}${window.encodeURIComponent(search)}` : `${origin}${uri}${hash}&accessKey=${getParam('accessKey')}`;
      redirectToLoginWithOrigin(originUrl);
      return;
    }

    if (/^\/\d+\/task(\/.+)?$/.test(pathname)) {
      navigate(pathname.replace(/^(\/\d+\/)task(\/.+)?$/, '$1cm/taskList$2'));
      return;
    }

    if (translation.type === translationType.NONE) {
      setLang(DEFAULTLANGUAGE);
    }
    
    if(orgId && isEB360MapEnabled && !idToken || true){
        getCASToken({organisationId: orgId })
        .then((token) => {
          setIdToken(token);
        })
    }
    
    setNavConfig({
      showLogin: !!orgName,
      showSignUp: !memberApp && !!orgName && isPublic,
      showCrisisTask: showCrisisTask && !!accountId,
      showCrisisSingleEventDashboard: showCrisisSingleEventDashboard && !!accountId,
      showCrisisSurvey: showCrisisSurvey && !!accountId,
      showNotification: showNotificationsToMemberPortal && (isPublic || (!isPublic && !!accountId)),
      showWeather,
      showIncidents,
      showOnCallSchedule,
      showEvent: accountId ? accountShowEvent : initShowEvent,
      showTravel: ebs_trm_enableSecuredTravelEducationPortal && showTravel && !!accountId, 
      showFAQs,
      showOverview,
      enabledSSO,
      showDirectoryChat,
      fromMemberApp: memberApp,
      accessKey,
      showPublicFilesLibrary,
      isLoggedIn: Object.keys(account).length !== 0,
    });
    Ajax.SET_X_CSRF_TOKEN(CSRF_TOKEN_FOR_SESSION);
  }, [bootData, account, splitData]);

  const [localeMessages, setLocaleMessages] = useState(enUS)
  useEffect(() => {
    const htmlDom = document.documentElement;
    import(`@eb/antd/es/locale/${lang}.js`).then((value) => {
      setLocaleMessages(value.default)
    })
    htmlDom.setAttribute('lang', lang.replace('_', '-'));
    const rtlLink = document.createElement('link');
    rtlLink.type = 'text/css';
    rtlLink.rel = 'stylesheet';
    rtlLink.href = `/public/css/styles_rtl.css?version=${FE_VERSION}`;
    rtlLink.id = 'EBRtlCss';
    if (lang === 'ar_SA') {
      htmlDom.setAttribute('dir', 'rtl');
      document.body.classList.add('rtl');
      document.head.appendChild(rtlLink);
    } else {
      htmlDom.setAttribute('dir', 'ltr');
      if (document.body.classList.contains('rtl')) {
        document.body.classList.remove('rtl');
      }
      const rtlCssDom = document.getElementById('EBRtlCss');
      if (rtlCssDom) {
        document.head.removeChild(rtlCssDom);
      }
    }
  }, [lang]);

  const onPaginationChange = (pageNo, pageSize) => {
    setPageNo(pageNo)
    setPageSize(pageSize)
  }

  if (isCommsDetail && commsFetchStatus && commsFetchStatus !== 200) {
    return (
      <I18NProvider url={getI18nUrl(lang)}>
        <CommsErrorPage />
      </I18NProvider>
    );
  }

  if (!bootData || !account || !navConfig) {
    if (bootDataHttpStatus === 404) {
      return (
        <I18NProvider url={getI18nUrl(lang)}>
          <ErrorPage />
        </I18NProvider>
      );
    }
    return null;
  }
  if (bootData.organization && bootData.organization.enableSSP === false) {
    if (/^\/\d+\/(login|new|forgot-username|forgot-password|notif|weather|event|ov|faq)(\/.*)*$/.test(window.location.pathname)) {
      return (
        <I18NProvider url={getI18nUrl(lang)}>
          <ErrorPage titleCode="error.label.page" messageCode="ssp.page.deactivated" />
        </I18NProvider>
      );
    }
  }

  // if commsDetail page and commsData is not fetched, then return null
  // this is for resolving the issue of showing the page before fetching commsData
  if (isCommsDetail &&
    (!commsData || (commsData && window.orgId !== commsData.result.organizationId))) {
    return null
  }

  return (
    <GlobalContext.Provider
      value={{
        fetchInitData,
        lang,
        changeLang: (newLang) => {
          if (newLang === lang) {
            return;
          }
          putNewLangToServer({ lang: newLang }, () => {
            cookies.set('lang', newLang, { path: '/' });
            for (let i = 0; i < window.frames.length; i += 1) {
              window.frames[i].postMessage({ newLang }, window.location.origin);
            }
            setLang(newLang);
          });
        },
        account: account || { id: 0 },
        navConfig,
        ...bootData,
        organization: {
          ...bootData.organization,
          userStatus: bootData.organization.isPublic ? 'public' : 'private',
        },
        isInLoggedInEditProfileMode,
        setIsInLoggedInEditProfileMode,
        pageNo,
        pageSize,
        onPaginationChange,
        flags: { ...splitData },
        csrfData: csrfResponse || null,

        commsData: commsData
          ? {
            ...commsData.result,
            commsId,
            isCommsDetail,
            orgId: commsData.result.organizationId,
          }
        : { isCommsDetail: false },
        shouldUseEB360Map: true, // TODO replace with organisation services.
        idToken,
        setIsPublicPortalEnabled, isPublicPortalEnabled
      }}
    >
      <I18NProvider url={getI18nUrl(lang)}>
        <ConfigProvider locale={localeMessages} direction={lang === 'ar_SA' ? 'rtl' : 'ltr'}>
          {!isCommsDetail && children}
          {isCommsDetail && commsData && orgId === commsData.result.organizationId && children}
        </ConfigProvider>
      </I18NProvider>
    </GlobalContext.Provider>
  );
};

GlobalProvider.propTypes = {
  children: PropTypes.element.isRequired,
}
